import { useRef } from 'react';

import { Button, type ButtonProps } from '../ui/Button';
import { Dialog } from '../ui/dialog';

import { type ResolvableModalFC } from './modalsManager';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resolve: (value: boolean) => void;
  title: string;
  description: string;
  confirmText: string;
  cancelText?: string;
  confirmVariant?: Extract<
    ButtonProps['variant'],
    'destructive' | 'warning' | 'default'
  >;
  // Buttons get `${testId}-confirm` / `${testId}-cancel`.
  testId?: string;
}

export const ConfirmDialog: ResolvableModalFC<ConfirmDialogProps, boolean> = ({
  open,
  onOpenChange,
  resolve,
  title,
  description,
  confirmText,
  cancelText = 'Cancel',
  confirmVariant = 'destructive',
  testId,
}) => {
  const hasConfirmed = useRef(false);

  const resolveParent = (value: boolean) => {
    hasConfirmed.current = true;
    resolve(value);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !hasConfirmed.current) {
      // only if the user has not confirmed, and the dialog is being closed
      resolveParent(false);
    }
    onOpenChange(open);
  };
  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content className="max-w-lg" data-testid={testId}>
        <Dialog.Header>
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description>{description}</Dialog.Description>
        </Dialog.Header>
        <Dialog.Footer className="mt-4 gap-2">
          <Button
            variant="outline"
            data-testid={testId ? `${testId}-cancel` : undefined}
            onClick={() => {
              onOpenChange(false);
              resolveParent(false);
            }}
          >
            {cancelText}
          </Button>

          <Button
            variant={confirmVariant}
            data-testid={testId ? `${testId}-confirm` : undefined}
            onClick={() => {
              onOpenChange(false);
              resolveParent(true);
            }}
          >
            {confirmText}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
